// Portfolio.jsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Github, X } from "lucide-react";
import datingappImg from "../assets/datingapp.jpeg";
import learnogicImg from "../assets/learnogic.jpeg";
import chatImg from "../assets/chat.jpg";
import artvileImg from "../assets/artvile.jpeg";
import vizentryImg from "../assets/vizentry.png";

const Portfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  const HERO_IMAGE_URL =
    "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop";

  // Portfolio projects data with project links
  const portfolioProjects = [
    {
      id: 1,
      title: "Dating App Platform",
      category: "Mobile Development",
      image: datingappImg,
      description:
        "A sophisticated mobile dating application with real-time matching, chat functionality, and advanced user preferences.",
      technologies: ["React Native", "Firebase", "Node.js", "Socket.io"],
      features: [
        "Real-time messaging and notifications",
        "AI-powered matching algorithm",
        "Geolocation-based discovery",
        "Video call integration",
      ],
      projectLinks: [], // No links for this project
      liveUrl: "#",
      githubUrl: "#",
      gradient: "from-pink-500 to-rose-500",
    },
    {
      id: 2,
      title: "Learnogic - Learning Platform",
      category: "Web Development",
      image: learnogicImg,
      description:
        "An interactive e-learning platform with course management, progress tracking, and interactive assessments.",
      technologies: ["React.js", "Node.js", "MongoDB", "Tailwind CSS"],
      features: [
        "Interactive course builder",
        "Progress tracking and analytics",
        "Live video sessions",
        "Certificate generation",
      ],
      projectLinks: [
        { label: "Production Site", url: "https://learnogic.com/" }
      ],
      liveUrl: "#",
      githubUrl: "#",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      id: 3,
      title: "Real-time Chat Application",
      category: "Web Development",
      image: chatImg,
      description:
        "A feature-rich chat application with group messaging, file sharing, and end-to-end encryption.",
      technologies: ["React.js", "WebSocket", "Redis", "PostgreSQL"],
      features: [
        "Real-time messaging",
        "Group chat functionality",
        "File and media sharing",
        "Message encryption",
      ],
      projectLinks: [], // No links for this project
      liveUrl: "#",
      githubUrl: "#",
      gradient: "from-purple-500 to-indigo-500",
    },
    {
      id: 4,
      title: "Artvile - Creative Marketplace",
      category: "UI/UX Design",
      image: artvileImg,
      description:
        "A vibrant marketplace for artists to showcase and sell their digital artwork with integrated payment solutions.",
      technologies: ["React.js", "Stripe", "AWS S3", "Express.js"],
      features: [
        "Artist portfolio management",
        "Secure payment processing",
        "Digital rights management",
        "Social engagement features",
      ],
      projectLinks: [
        { label: "Artville Academy", url: "https://www.artvilleacademy.com/" }
      ],
      liveUrl: "#",
      githubUrl: "#",
      gradient: "from-orange-500 to-red-500",
    },
    {
      id: 5,
      title: "Vizentry - Visitor Management",
      category: "Desktop Applications",
      image: vizentryImg,
      description:
        "Enterprise visitor management system with facial recognition, badge printing, and comprehensive analytics.",
      technologies: ["Electron", "Python", "TensorFlow", "SQLite"],
      features: [
        "Facial recognition check-in",
        "Automated badge printing",
        "Visitor analytics dashboard",
        "Integration with access control",
      ],
      projectLinks: [
        { label: "Visit Site", url: "https://vizentry.com/" },
      ],
      liveUrl: "#",
      githubUrl: "#",
      gradient: "from-teal-500 to-green-500",
    },
  ];

  // Hero Section
  const HeroSection = () => (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-black">
        <img
          src={HERO_IMAGE_URL}
          alt="Portfolio Background"
          className="w-full h-full object-cover opacity-50 grayscale hover:grayscale-0 transition-all duration-1000"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <span className="text-cyan-400 text-sm font-semibold tracking-widest uppercase bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full border border-cyan-500/20">
            OUR PORTFOLIO
          </span>
        </motion.div>

        <motion.h1
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight"
        >
          <span className="text-white drop-shadow-lg">Innovating the</span>
          <br />
          <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent drop-shadow-lg">
            Digital Future
          </span>
        </motion.h1>

        <motion.p
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto mb-16 leading-relaxed drop-shadow-md"
        >
          We are a team of passionate technologists, designers, and strategists
          committed to transforming businesses through innovative digital
          solutions.
        </motion.p>
      </div>

      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-gray-300 text-xs uppercase tracking-wider drop-shadow-md">
            Scroll
          </span>
          <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center p-2 bg-black/20 backdrop-blur-sm">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-1.5 h-3 bg-gradient-to-b from-cyan-400 to-purple-600 rounded-full"
            />
          </div>
        </div>
      </motion.div>
    </section>
  );

  // Projects Grid
  const ProjectsGrid = () => (
    <section className="py-24 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Featured{" "}
            <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent">
              Projects
            </span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Discover how we've helped businesses transform their digital
            presence with innovative solutions
          </p>
        </motion.div>

        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {portfolioProjects.map((project, index) => (
            <motion.div
              key={project.id}
              layout
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              onClick={() => setSelectedProject(project)}
              className="group cursor-pointer bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-cyan-500/50 shadow-lg hover:shadow-cyan-500/20 transition-all duration-300"
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

                <div className="absolute top-4 left-4">
                  <span
                    className={`px-3 py-1 bg-gradient-to-r ${project.gradient} text-white text-xs font-semibold rounded-full shadow-lg`}
                  >
                    {project.category}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                  {project.title}
                </h3>

                <p className="text-gray-400 text-sm mb-4 line-clamp-2 leading-relaxed">
                  {project.description}
                </p>

                <div className="flex flex-wrap gap-2">
                  {project.technologies.slice(0, 3).map((tech, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-gray-800 text-gray-300 text-xs font-medium rounded-full border border-gray-700 hover:border-cyan-500/50 hover:text-cyan-400 transition-colors"
                    >
                      {tech}
                    </span>
                  ))}
                  {project.technologies.length > 3 && (
                    <span className="px-3 py-1 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 text-xs font-medium rounded-full border border-cyan-500/30">
                      +{project.technologies.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );

  // Project Modal
  const ProjectModal = () => {
    if (!selectedProject) return null;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setSelectedProject(null)}
        className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-gray-900 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-800"
        >
          {/* Modal Header */}
          <div className="relative h-80">
            <img
              src={selectedProject.image}
              alt={selectedProject.title}
              className="w-full h-full object-cover"
            />
            <div
              className={`absolute inset-0 bg-gradient-to-t ${selectedProject.gradient} opacity-70`}
            />

            <button
              onClick={() => setSelectedProject(null)}
              className="absolute top-4 right-4 p-2 bg-white/10 backdrop-blur-sm rounded-full shadow-lg hover:bg-white/20 transition-colors border border-white/20"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            <div className="absolute bottom-6 left-6 right-6 text-white">
              <h2 className="text-4xl md:text-5xl font-bold mb-3">
                {selectedProject.title}
              </h2>
              <span className="inline-block px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-sm font-semibold border border-white/30">
                {selectedProject.category}
              </span>
            </div>
          </div>

          {/* Modal Content */}
          <div className="p-8">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-white mb-4">
                Project Overview
              </h3>
              <p className="text-gray-300 text-lg leading-relaxed">
                {selectedProject.description}
              </p>
            </div>

            {/* Project Links Section - NEW */}
            {selectedProject.projectLinks && selectedProject.projectLinks.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-bold text-white mb-4">
                  Live Project Links
                </h3>
                <div className="flex flex-wrap gap-3">
                  {selectedProject.projectLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r ${selectedProject.gradient} text-white rounded-lg font-medium shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200`}
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>{link.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Features */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-white mb-4">
                Key Features
              </h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {selectedProject.features.map((feature, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-gray-300"
                  >
                    <div
                      className={`mt-1 w-5 h-5 rounded-full bg-gradient-to-r ${selectedProject.gradient} flex items-center justify-center flex-shrink-0`}
                    >
                      <div className="w-2 h-2 bg-white rounded-full" />
                    </div>
                    <span className="leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Technologies */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-white mb-4">
                Technologies Used
              </h3>
              <div className="flex flex-wrap gap-3">
                {selectedProject.technologies.map((tech, index) => (
                  <span
                    key={index}
                    className={`px-5 py-2.5 bg-gradient-to-r ${selectedProject.gradient} text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-shadow`}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-black">
      <HeroSection />
      <ProjectsGrid />
      {selectedProject && <ProjectModal />}
    </div>
  );
};

export default Portfolio;
